import { List } from "immutable";

import { Dispatch, GetState } from "common/types";

import { getClient } from "services";
import errorHandler from "services/classService/errorHandler";

import {
  fetchCurrentStudentAssignments,
  fetchStudentAssignmentIfNotFetched
} from "seneca-common/features/assignments/features/student-assignments/service";
import {
  deleteAssignments,
  makeGetClassAssignmentIds
} from "seneca-common/features/assignments/features/student-assignments/state";
import { resetFetchStudentAssignmentAction } from "seneca-common/features/assignments/features/student-assignments/state/actions/meta";
import { getAllContents } from "seneca-common/features/course/state";
import { isSignedIn } from "seneca-common/features/user/state";

import {
  childClassEnrollmentsNeedsFetching,
  classEnrollmentNeedsFetching,
  classEnrollmentsNeedFetching
} from "../../selectors";
import {
  createClassEnrollmentActionFactory,
  deleteClassEnrollment,
  deleteClassEnrollmentActionFactory,
  fetchAllClassesEnrollmentsActionFactory,
  fetchChildClassEnrollmentsActionFactory,
  fetchClassEnrollmentActionFactory,
  isEnrolledInClass,
  receiveChildClassEnrollments,
  receiveClassEnrollment,
  receiveClassEnrollments
} from "../../state";

const TIMEOUT_BEFORE_FETCHING_ASSIGNMENTS = 2000;

const getClassServiceClient = () => getClient("classService");

export const fetchChildClassEnrollmentsAction =
  fetchChildClassEnrollmentsActionFactory({
    requestFunction: (userId: string) =>
      getClassServiceClient().fetchChildClassEnrollments(userId),
    storeAction: receiveChildClassEnrollments
  });

function fetchChildClassEnrollments(userId: string) {
  return fetchChildClassEnrollmentsAction([userId], userId);
}

export const fetchChildClassEnrollmentsIfNotFetched =
  ({ userId }: { userId: string }) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    if (childClassEnrollmentsNeedsFetching(state, userId)) {
      return dispatch(fetchChildClassEnrollments(userId));
    }
  };

export const fetchEnrolledClasses = ({
  schoolId
}: { schoolId?: string } = {}) =>
  fetchAllClassesEnrollmentsActionFactory({
    requestFunction: schoolId =>
      getClassServiceClient().fetchUsersClassEnrollments({ schoolId }),
    storeAction: receiveClassEnrollments
  })(schoolId, schoolId);

const fetchEnrolledClassAction = fetchClassEnrollmentActionFactory({
  requestFunction: classId =>
    getClassServiceClient().fetchUsersClassEnrollment(classId),
  storeAction: receiveClassEnrollment
});

const saveNewClassEnrollmentAction = createClassEnrollmentActionFactory({
  requestFunction: classId =>
    getClassServiceClient().enrollUserInClass(classId),
  errorHandler
});

const deleteClassEnrollmentAction = deleteClassEnrollmentActionFactory({
  requestFunction: classId =>
    getClassServiceClient().unenrollUserFromClass(classId),
  errorHandler
});

export const fetchEnrolledClassesIfNotFetched =
  ({ schoolId }: { schoolId?: string } = {}) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    if (classEnrollmentsNeedFetching(state)) {
      return dispatch(fetchEnrolledClasses({ schoolId }));
    }
  };

export const fetchEnrolledClass = (classId: string) => {
  return (dispatch: Dispatch) => {
    return dispatch(fetchEnrolledClassAction(classId, classId));
  };
};

export const fetchEnrolledClassIfNotFetched =
  (classId: string) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    if (classEnrollmentNeedsFetching(state, classId)) {
      return dispatch(fetchEnrolledClass(classId));
    }
  };

export const saveNewClassEnrollment = (classId: string) => {
  return (dispatch: Dispatch) => {
    return dispatch(saveNewClassEnrollmentAction(classId, classId));
  };
};

export const saveNewClassEnrollmentIfNotAlreadyEnrolled = (
  classId: string,
  assignmentId?: string
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const contents = getAllContents(state);
    const signedIn = isSignedIn(state);
    if (signedIn) {
      await dispatch(fetchEnrolledClassesIfNotFetched());
      const stateWithEnrollments = getState();
      const alreadyEnrolled = isEnrolledInClass(stateWithEnrollments, classId);
      if (!alreadyEnrolled) {
        const enrolmentRepsonse = await dispatch(
          saveNewClassEnrollment(classId)
        );
        if (!(enrolmentRepsonse instanceof Error)) {
          if (assignmentId) {
            dispatch(resetFetchStudentAssignmentAction(assignmentId));
          }

          setTimeout(() => {
            if (assignmentId) {
              dispatch(fetchStudentAssignmentIfNotFetched(assignmentId));
            } else {
              dispatch(fetchCurrentStudentAssignments());
            }
          }, TIMEOUT_BEFORE_FETCHING_ASSIGNMENTS);
        }

        return enrolmentRepsonse;
      }
    }
  };
};

export function unenrollFromClass(classId: string) {
  return async (dispatch: Dispatch, getState: GetState) => {
    const output = await dispatch(
      deleteClassEnrollmentAction(classId, classId)
    );
    if (!(output instanceof Error)) {
      dispatch(deleteClassEnrollment(classId));
      const state = getState();
      const getClassAssignmentsIds = makeGetClassAssignmentIds(classId);
      const assignmentIds = getClassAssignmentsIds(state) as List<string>;
      dispatch(deleteAssignments(assignmentIds));
    }
  };
}
